import React, { useContext, useState, useEffect } from "react";
import { TerritoryRegion } from "../types/territoryRegion";
import { useAuth } from "../Auth/AuthProvider";
import useSWRImmutable from "swr/immutable";
import { fetcherUserTerritory } from "../utils/fetcher";
import { event } from "../utils/gtag";
import { useKpiVisibility } from "../kpis/useKpiVisibility";
import { useStores } from "./storesContext";


type TerritoryContextType = {
  selectTerritory: (key: string) => void;
  selectedTerritory: TerritoryRegion | null;
  territories: TerritoryRegion[];
};

const TerritoryContext = React.createContext<TerritoryContextType>({
  selectTerritory: () => {},
  territories: [],
  selectedTerritory: null,
});

type TerritoryProviderProps = {
  children: React.ReactNode;
};

export const TerritoryProvider: React.FunctionComponent<
  TerritoryProviderProps
> = ({ children }: TerritoryProviderProps) => {
  const { user } = useAuth();
  const { isModuleExists } = useKpiVisibility();
  const { selectedStore } = useStores();
  const [territories, setTerritories] = useState<Array<TerritoryRegion>>([]);
  const [selectedTerritory, setSelectedTerritory] = useState<TerritoryRegion | null>(
    territories?.[0] ?? null
  );
  

  // new fetch request user store with swr
  const { data: userTerritory } = useSWRImmutable<TerritoryRegion[]>(
    () =>
      user && isModuleExists("Territoire")
        ? [
            `/territories-and-regions`,
          ]
        : null,
    ([url]) => fetcherUserTerritory(url)
  );

  useEffect(() => {
    if (userTerritory === undefined) return;

    let indexLocal = 0;
    const storedIndex = localStorage.getItem("indexTerritoryData");

    if (storedIndex !== null) {
      indexLocal = JSON.parse(storedIndex) as number;
    } else if (selectedStore?.storeKey) {
      const matchingIndex = userTerritory.findIndex((territory) =>
        territory.stores?.some((ts) => ts.storeKey === selectedStore.storeKey)
      );
      if (matchingIndex !== -1) {
        indexLocal = matchingIndex;
      }
    }

    setSelectedTerritory(userTerritory?.[indexLocal] || null);
    setTerritories(userTerritory as TerritoryRegion[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTerritory]);

  const selectTerritory = (key: string) => {
    const index = territories.findIndex(
      (territoryRegion: TerritoryRegion) => territoryRegion.key === key
    );
    setSelectedTerritory(territories[index] ?? null);
    const current = territories[index];
    if (current) {
      event({
        action: "Select_Territory",
        value: current?.name,
      });
    }
    localStorage.setItem("indexTerritoryData", JSON.stringify(index));
    return index;
  };

  return (
    <TerritoryContext.Provider
      value={{ selectTerritory, territories, selectedTerritory }}
    >
      {children}
    </TerritoryContext.Provider>
  );
};

export const useTerritories: () => TerritoryContextType = () =>
  useContext(TerritoryContext);
